import React from 'react';

import {resource} from '@albert-io/json-api-framework/request/builder';
import useEnrollStudent from 'client/StudentHomepage/hooks/useEnrollStudent';
import {useStudentHomepageStore} from 'client/StudentHomepage/useStudentHomepageStore';

interface Props extends PropsWithChildrenRequired, PropsWithClassNameOptional {
  onEnrollSuccess?: () => void;
}

export default function JoinClassroomForm({children, className, onEnrollSuccess}: Props) {
  const classrooms = useStudentHomepageStore((state) => state.classrooms);
  const isError = useStudentHomepageStore((state) => state.isStudentEnrollmentError);
  const isRequestPending = useStudentHomepageStore((state) => state.isStudentEnrollmentPending);
  const setDuplicateClassroom = useStudentHomepageStore((state) => state.setDuplicateClassroom);
  const value = useStudentHomepageStore((state) => state.classroomCode);
  const enrollStudent = useEnrollStudent();

  async function handleSubmit() {
    const relationship = {
      meta: {
        classroom_code: value.trim()
      },
      type: 'classroom_v1'
    };
    enrollStudent({
      relationship,
      onReject: async (err) => {
        if (err.response?.status === 422) {
          const classWithMatchingCode = classrooms.find(
            (classroom) => classroom.getCode() === value
          );

          if (classWithMatchingCode) {
            setDuplicateClassroom(classWithMatchingCode);
            return;
          }

          resource('student_classroom_v1')
            .mandarkEndpoint(['student_classrooms_v1'])
            .findOne()
            .filter({code: value})
            .getResourcePromise()
            .then((classWithMatchingCodeFromServer) => {
              setDuplicateClassroom(classWithMatchingCodeFromServer);
            });
        }
        // A non-422 failure (e.g. an invalid/expired code) is already surfaced to the
        // student via `setIsStudentEnrollmentError` in `useEnrollStudent`; re-throwing
        // here only escaped as an unhandled promise rejection that flooded Honeybadger
        // (fault #130630365), so we intentionally do not rethrow.
      },
      onResolve: () => {
        onEnrollSuccess?.();
      }
    });
  }

  return (
    <form
      aria-label='classroom enrollment form'
      className={className}
      onSubmit={(e) => {
        e.preventDefault();
        if (isRequestPending || isError || value === '') {
          return;
        }
        handleSubmit();
      }}
    >
      {children}
    </form>
  );
}
